/**
 * generate-local-embedding
 *
 * Local text embedding generation using Xenova transformers.
 *
 * Primary responsibilities:
 * - Load the feature-extraction pipeline once per process
 * - Keep model caches on writable serverless storage
 *
 * Dependencies:
 * - @xenova/transformers
 * - lib/ai/transformers-cache
 */

import {
  configureTransformersProcessCacheEnv,
  configureTransformersRuntimeCache
} from '@/lib/ai/transformers-cache'

type EmbeddingOutput = {
  data: ArrayLike<number> | Iterable<number>
}

type FeatureExtractionPipeline = (
  content: string,
  options: {
    normalize: true
    pooling: 'mean'
  }
) => Promise<EmbeddingOutput>

configureTransformersProcessCacheEnv()

let featureExtractionPipelinePromise:
  | Promise<FeatureExtractionPipeline>
  | null = null

async function getFeatureExtractionPipeline() {
  if (!featureExtractionPipelinePromise) {
    featureExtractionPipelinePromise = import('@xenova/transformers').then(
      async (transformers) => {
        configureTransformersRuntimeCache(transformers)

        return transformers.pipeline(
          'feature-extraction',
          'Xenova/all-MiniLM-L6-v2'
        ) as Promise<FeatureExtractionPipeline>
      }
    )
  }

  return featureExtractionPipelinePromise
}

export async function generateLocalEmbedding(content: string): Promise<number[]> {
  const generateEmbedding = await getFeatureExtractionPipeline()

  const output = await generateEmbedding(content, {
    pooling: 'mean',
    normalize: true
  })

  return Array.from(output.data)
}
